import { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, getDocs, writeBatch, Timestamp, limit } from 'firebase/firestore';

const DeleteOrderRecords = () => {
  const collectionName = 'orders'; 
  const dateFieldName = 'createdDateTime'; 
  const [selectedDate, setSelectedDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [records, setRecords] = useState([]);
  const [result, setResult] = useState({ status: '', count: 0 });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [displayFields, setDisplayFields] = useState([]);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setRecords([]);
    setResult({ status: '', count: 0 });
    setShowConfirmation(false);
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    
    try {

      if (value.toDate) {
        return value.toDate().toLocaleString();
      } 

      else if (value instanceof Date) {
        return value.toLocaleString();
      }
      else if (typeof value === 'string') {
        return new Date(value).toLocaleString();
      } else {
        return 'Invalid Date';
      }
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const extractDisplayFields = (record) => {
    if (!record) return ['id'];
    
    const fields = ['id'];
    
    Object.keys(record).forEach(key => {
      if (key !== 'id') {
        fields.push(key);
      }
    });
    
    return fields;
  };

  const formatValue = (value, fieldName) => {
    if (value === null || value === undefined) return 'N/A';
    
    if (fieldName === dateFieldName || fieldName.toLowerCase().includes('date') || fieldName.toLowerCase().includes('time')) {
      return formatDate(value);
    }
    
    if (value instanceof Object) {
      if (value.toDate) {
        return formatDate(value);
      }
      
      try {
        return JSON.stringify(value);
      } catch (e) {
        return '[Complex Object]';
      }
    }
    
    return String(value);
  };

  const fetchRecords = async () => {
    if (!selectedDate) return;
    
    try {
      setIsLoading(true);
      setRecords([]);
      setResult({ status: '', count: 0 });
      setDisplayFields([]);
      
      const db = getFirestore();
      
      const targetDate = new Date(selectedDate);
      targetDate.setHours(0, 0, 0, 0);
      
      const recordsRef = collection(db, collectionName);
      const countQuery = query(recordsRef, where(dateFieldName, '<', targetDate.toISOString()));
      const previewQuery = query(
        recordsRef, 
        where(dateFieldName, '<', targetDate.toISOString()),
      );

      const countSnapshot = await getDocs(countQuery);
      setTotalCount(countSnapshot.size);
      
      const previewSnapshot = await getDocs(previewQuery);
      const fetchedRecords = [];
      
      previewSnapshot.forEach((doc) => {
        const data = doc.data();
        fetchedRecords.push({
          id: doc.id,
          ...data
        });
      });
      
      setRecords(fetchedRecords);
      
      if (fetchedRecords.length > 0) {
        setDisplayFields(extractDisplayFields(fetchedRecords[0]));
      }
      
      if (countSnapshot.size === 0) {
        setResult({ status: 'noRecords', count: 0 });
      }
    } catch (error) {
      console.error("Error fetching records: ", error);
      setResult({ status: 'error', message: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchRecords = () => {
    fetchRecords();
  };

  const handleConfirmDelete = () => {
    setShowConfirmation(true);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const handleDelete = async () => {
    if (!selectedDate || totalCount === 0) return;
    
    try {
      setIsDeleting(true);
      setResult({ status: 'processing', count: 0 });
      
      const db = getFirestore();
      const batchSize = 500;
      
      const targetDate = new Date(selectedDate);
      targetDate.setHours(0, 0, 0, 0);
      
      const recordsRef = collection(db, collectionName);
      const q = query(recordsRef, where(dateFieldName, '<', targetDate.toISOString()));
      
      const querySnapshot = await getDocs(q);
      const totalDocuments = querySnapshot.size;
      
      if (totalDocuments === 0) {
        setResult({ status: 'complete', count: 0 });
        setIsDeleting(false);
        setShowConfirmation(false);
        return;
      }
      
      let processed = 0;
      let batch = writeBatch(db);
      let batchCount = 0;
      
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
        batchCount++;
        processed++;
        
        if (batchCount >= batchSize) {
          batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      });
      
      if (batchCount > 0) {
        await batch.commit();
      }
      
      setResult({ status: 'complete', count: processed });
      setRecords([]);
      setTotalCount(0);
    } catch (error) {
      console.error("Error deleting records: ", error);
      setResult({ status: 'error', message: error.message });
    } finally {
      setIsDeleting(false);
      setShowConfirmation(false);
    }
  };

  return (
    <div className="p-4 bg-gray-100 rounded-lg shadow">
      <h3 className="text-lg font-medium mb-4">Delete Order Records Before Date</h3>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Select Date:
        </label>
        <input
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          className="border rounded p-2 w-full"
          disabled={isDeleting || isLoading}
        />
      </div>
      
      <button
        onClick={handleFetchRecords}
        disabled={!selectedDate || isLoading || isDeleting}
        className={`w-full py-2 px-4 rounded mb-4 ${
          !selectedDate || isLoading || isDeleting
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
        }`}
      >
        {isLoading ? 'Loading...' : 'Preview Orders'}
      </button>
      
      {records.length > 0 && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium">Orders Preview</h4>
            <span className="text-sm text-gray-600">
              Showing {records.length} of {totalCount} total orders
            </span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border">
              <thead>
                <tr className="bg-gray-200">
                  {displayFields.map((field) => (
                    <th key={field} className="py-2 px-3 text-left text-sm font-medium text-gray-700 border-b">
                      {field.charAt(0).toUpperCase() + field.slice(1).replace(/\./g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((record, index) => (
                  <tr key={record.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    {displayFields.map((field) => (
                      <td key={`${record.id}-${field}`} className="py-2 px-3 text-sm border-b">
                        {field === 'id' ? record.id : formatValue(record[field], field)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {!showConfirmation && totalCount > 0 && (
            <button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="mt-4 w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded"
            >
              Delete All {totalCount} Orders
            </button>
          )}
        </div>
      )}
      
      {result.status === 'noRecords' && (
        <div className="mt-4 p-3 bg-blue-100 text-blue-800 rounded">
          No orders found before {selectedDate}.
        </div>
      )}
      
      {showConfirmation && (
        <div className="space-y-4">
          <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 text-yellow-700">
            <p className="font-bold">Warning!</p>
            <p>You are about to permanently delete all {totalCount} orders created before {selectedDate}. This action cannot be undone.</p>
            <p>Collection: <span className="font-medium">{collectionName}</span></p>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="w-1/2 py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded"
            >
              {isDeleting ? 'Deleting...' : 'Confirm Delete'}
            </button>
            
            <button
              onClick={handleCancel}
              disabled={isDeleting}
              className="w-1/2 py-2 px-4 bg-gray-500 hover:bg-gray-600 text-white rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {result.status === 'complete' && (
        <div className="mt-4 p-3 bg-green-100 text-green-800 rounded">
          Successfully deleted {result.count} orders before {selectedDate}.
        </div>
      )}
      
      {result.status === 'error' && (
        <div className="mt-4 p-3 bg-red-100 text-red-800 rounded">
          Error: {result.message}
        </div>
      )}
    </div>
  );
};

export default DeleteOrderRecords;